import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { translationKeys } from '../../../core';
import { DashboardSettings, NotificationService, ProfileService, ScipUiSettingsService } from '../../../services';
import { map, take } from 'rxjs/operators';
import { AvanoLoadingIndicatorService, ComboboxEntry } from '@avano/avano-components';
import { FormBuilder } from '@angular/forms';
import { forkJoin } from 'rxjs';

import { OrganizationDto } from '@scip/api';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';

@Component({
  selector: 'scip-dashboard-user-settings',
  templateUrl: './dashboard-user-settings.component.html',
  styleUrls: ['./dashboard-user-settings.component.scss']
})
export class DashboardUserSettingsComponent implements OnInit {
  @Output() readonly cancelEvent: EventEmitter<void> = new EventEmitter<void>();

  showDialog = true;
  translationKeys: typeof translationKeys = translationKeys;
  selectableOrganizations: ComboboxEntry[] = [];
  selectedOrg: any;
  blockingIdentifier = 'dashboardUserSettings';

  // The form has the same nesting like the dashboardSettings-object
  // That way we can just use the value of the form directly to update the object
  dashboardSettingsForm = this.fb.group({
    selectedOrganization: {
      selectedOrgLabel: null,
      value: {
        name: null,
        id: null
      }
    },
    tableTiles: this.fb.group({
      show: [],
      failedSubmissions: this.fb.group({
        show: []
      }),
      productsWithoutSubmissions: this.fb.group({
        show: []
      })
    }),
    chartTiles: this.fb.group({
      show: [],
      submissionsByStatus: this.fb.group({
        show: []
      }),
      productsBySubmissionsStatus: this.fb.group({
        show: []
      }),
      submissionByYear: this.fb.group({
        show: []
      }),
      submissionByOrganisation: this.fb.group({
        show: []
      })
    }),
    other: this.fb.group({
      recentSearches: this.fb.group({
        show: []
      })
    })
  });

  constructor(
    private notificationService: NotificationService,
    private scipUiSettings: ScipUiSettingsService,
    private avanoBlocker: AvanoLoadingIndicatorService,
    private profileService: ProfileService,
    private translateService: TranslateService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.avanoBlocker.block([this.blockingIdentifier]);
    forkJoin([
      this.profileService.getAllOrganizations(true, false, false, false).pipe(take(1)),
      this.scipUiSettings.getScipUiSettings().pipe(
        take(1),
        // We do not need the whole ui settings, we only need the dashboard specific ones
        map((wholeUiSettings) => wholeUiSettings.dashboard)
      )
    ]).subscribe(([organizations, dashboardSettings]: [OrganizationDto[], DashboardSettings]) => {
      this.selectableOrganizations = organizations.map((singleOrg: OrganizationDto) => {
        return {
          label: singleOrg.name,
          value: singleOrg
        };
      });
      this.selectableOrganizations.push({
        label: this.translateService.instant(translationKeys.dashboard.userSettingsComponent.organizationDropdown.allOrganisations),
        value: {
          name: '',
          id: ''
        }
      });
      const selectedOrganization = this.selectableOrganizations.find((org) => org.value.identifier === dashboardSettings.selectedOrganization.value.id);
      if (selectedOrganization) {
        this.selectedOrg = selectedOrganization;
      } else {
        this.selectedOrg = this.selectableOrganizations.find((option) => option.value.id === '');
      }
      this.dashboardSettingsForm.patchValue(dashboardSettings);
      this.avanoBlocker.unblock([this.blockingIdentifier]);
    });
  }

  onOrgSelection(): void {
    if (this.selectedOrg) {
      this.dashboardSettingsForm.patchValue({
        selectedOrganization: {
          selectedOrgLabel: this.selectedOrg.name,
          value: {
            name: this.selectedOrg.name,
            id: this.selectedOrg.identifier
          }
        }
      });
    } else {
      this.dashboardSettingsForm.patchValue({
        selectedOrganization: {
          selectedOrgLabel: this.translateService.instant(translationKeys.dashboard.userSettingsComponent.organizationDropdown.allOrganisations),
          value: {
            name: '',
            id: ''
          }
        }
      });
      //setTimeout used to prevent timing issue for clearing org selection. If we don't wait the selection will be cleared after all organizations is set.
      setTimeout(() => (this.selectedOrg = this.selectableOrganizations.find((option) => option.value.id === '')), 200);
    }
  }

  saveSettings(): void {
    this.avanoBlocker.block([this.blockingIdentifier]);
    this.scipUiSettings.saveDashboardSettings(this.dashboardSettingsForm.value).subscribe((saved) => {
      if (saved) {
        this.notificationService.showSuccess(translationKeys.dashboard.userSettingsComponent.typescript.save.success);
        this.cancelEvent.emit();
      } else {
        this.avanoBlocker.unblock([this.blockingIdentifier]);
      }
    });
  }
}
